import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Rating,
  Chip,
  Alert,
  Link,
  Divider,
} from '@mui/material';
import {
  Edit,
  Delete,
  OpenInNew,
  Favorite,
  FavoriteBorder,
  ArrowBack,
  Add,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { sourcesApi } from '../services/api';
import { Source } from '../types';
import { useAuth } from '../context/AuthContext';

const SourceDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [source, setSource] = useState<Source | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if the current user owns this source
  const isSourceOwner = source && user ? source.addedByUserId === user.userId : false;

  useEffect(() => {
    const fetchSource = async () => {
      if (!id) return;
      
      setLoading(true);
      try {
        const sourceData = await sourcesApi.getSource(parseInt(id));
        setSource(sourceData);
      } catch (err) {
        console.error('Failed to fetch source:', err);
        setError('Failed to load source details');
      } finally {
        setLoading(false);
      }
    };

    fetchSource();
  }, [id]);

  const handleToggleFavorite = async () => {
    if (!source) return;

    try {
      const updatedSource = await sourcesApi.toggleFavorite(source.id);
      setSource(updatedSource);
    } catch (err) {
      console.error('Failed to toggle favorite:', err);
    }
  };

  const handleDelete = async () => {
    if (!source) return;

    if (window.confirm(`Are you sure you want to delete "${source.name}"?`)) {
      try {
        await sourcesApi.deleteSource(source.id);
        navigate('/sources');
      } catch (err) {
        console.error('Failed to delete source:', err);
        setError('Failed to delete source');
      }
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" gutterBottom>
          Loading Source Details...
        </Typography>
      </Container>
    );
  }

  if (error || !source) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || 'Source not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={() => navigate('/sources')}
        >
          Back to Sources
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/sources')}
          >
            Back to Sources
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/sources/add')}
          >
            Add Resource
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {source.name}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={source.isFavorite ? <Favorite /> : <FavoriteBorder />}
              onClick={handleToggleFavorite}
              color={source.isFavorite ? 'error' : 'inherit'}
            >
              {source.isFavorite ? 'Unfavorite' : 'Favorite'}
            </Button>

            {isSourceOwner && (
              <>
                <Button
                  variant="outlined"
                  startIcon={<Edit />}
                  onClick={() => navigate(`/sources/${source.id}/edit`)}
                >
                  Edit
                </Button>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Typography variant="body1" paragraph>
                {source.description || 'No description provided'}
              </Typography>

              {source.url && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Link
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<OpenInNew />}
                    component={Link}
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ mb: 2 }}
                  >
                    Open Source
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    {source.url}
                  </Typography>
                </>
              )}

              {source.personalNotes && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    Personal Notes
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {source.personalNotes}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Source Details
              </Typography>

              {source.author && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Author
                  </Typography>
                  <Typography variant="body1">
                    {source.author.name}
                    {source.author.website && (
                      <>
                        {' - '}
                        <Link href={source.author.website} target="_blank" rel="noopener noreferrer">
                          {source.author.website}
                        </Link>
                      </>
                    )}
                  </Typography>
                  {source.author.bio && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      {source.author.bio}
                    </Typography>
                  )}
                </Box>
              )}

              {source.sourcePlatform && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Platform
                  </Typography>
                  <Typography variant="body1">
                    {source.sourcePlatform}
                  </Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Rating
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating value={source.rating} size="small" readOnly />
                  <Typography variant="body2" sx={{ ml: 1 }}>
                    ({source.rating}/5)
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Added By
                </Typography>
                <Typography variant="body1">
                  {source.addedByUserName}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Date Added
                </Typography>
                <Typography variant="body1">
                  {new Date(source.dateAdded).toLocaleDateString()}
                </Typography>
              </Box>

              {source.sourceDate && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Source Date
                  </Typography>
                  <Typography variant="body1">
                    {new Date(source.sourceDate).toLocaleDateString()}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {source.tags.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Tags
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {source.tags.map((tag) => (
                    <Chip
                      key={tag.id}
                      label={tag.name}
                      sx={{
                        backgroundColor: tag.color || '#e0e0e0',
                        color: '#fff',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate(`/sources?tag=${tag.name}`)}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default SourceDetails;